import { useCallback, useEffect, useRef, useState } from "react";
import type { Organization, Patient } from "fhir/r5";
import { oids } from "@/configs/Settings";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { setEcommerceResourcesPreloadStatus } from "../helpers/ecommerceResourcesPreloadStatus";
import { ecommercePatientsService } from "../services/ecommercePatientsService";

const EMPTY_PATIENTS: Patient[] = [];

export interface UseEcommercePatientsResult {
  cnpj: string | undefined;
  patients: Patient[];
  loading: boolean;
  error: boolean;
  resourcesPreloaded: boolean;
  preloadFailed: boolean;
  /** true se o batch de MedicationDispense hidratou o cache de liberações. */
  dispensesPreloaded: boolean;
  /** true desde o mount com CNPJ até a primeira resposta (lista ou erro). */
  isInitialLoading: boolean;
  /** true enquanto a pré-carga de recursos (docs/liberações) está em andamento. */
  isPreloading: boolean;
  /** Pacientes já chegaram (sucesso ou lista vazia). */
  isListReady: boolean;
  /** Lista pronta e pré-carga concluída (ou falhou de forma controlada). */
  isFullyReady: boolean;
  /** Evita alerta vazio no primeiro render, antes do fetch. */
  hasFetchedOnce: boolean;
  /** Incrementa após pré-carga ok ou ao voltar do detalhe — reavalia filtros. */
  filterTick: number;
  bumpFilterTick: () => void;
  fetchPatients: () => Promise<void>;
}

export function useEcommercePatients(
  organization: Organization | null | undefined,
  listRevision = 0,
): UseEcommercePatientsResult {
  const cnpj = organization?.identifier?.find((identifier) =>
    identifier.system?.includes(oids.cnpj),
  )?.value;

  const organizationName = organization?.name || "";
  const organizationNameRef = useRef(organizationName);
  organizationNameRef.current = organizationName;
  const generationRef = useRef(0);

  const [patients, setPatients] = useState<Patient[]>(EMPTY_PATIENTS);
  const [resourcesPreloaded, setResourcesPreloaded] = useState(false);
  const [preloadFailed, setPreloadFailed] = useState(false);
  const [dispensesPreloaded, setDispensesPreloaded] = useState(false);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [filterTick, setFilterTick] = useState(0);

  const loadPatientsTask = useCallback((cnpjValue: string) => {
    return ecommercePatientsService.loadConsentPatients(cnpjValue);
  }, []);

  const {
    run: runLoadPatients,
    loading,
    error: loadError,
  } = useAsyncTask<[string], Patient[]>({
    task: loadPatientsTask,
    initialData: EMPTY_PATIENTS,
  });

  const preloadTask = useCallback(
    (
      loadedPatients: Patient[],
      organizationId: string,
      orgName: string,
    ) =>
      ecommercePatientsService.preloadPatientResources({
        patients: loadedPatients,
        organizationId,
        organizationName: orgName,
      }),
    [],
  );

  const { run: runPreload } = useAsyncTask<
    [Patient[], string, string],
    { ok: boolean; dispensesPreloaded: boolean }
  >({
    task: preloadTask,
  });

  const bumpFilterTick = useCallback(() => {
    setFilterTick((tick) => tick + 1);
  }, []);

  const fetchPatients = useCallback(async () => {
    if (!cnpj) return;

    const generation = ++generationRef.current;
    setResourcesPreloaded(false);
    setPreloadFailed(false);
    setDispensesPreloaded(false);
    setEcommerceResourcesPreloadStatus("loading");

    try {
      const loadedPatients = await runLoadPatients(cnpj);
      if (generation !== generationRef.current) return;
      setPatients(loadedPatients);
      setHasFetchedOnce(true);

      try {
        const resources = await runPreload(
          loadedPatients,
          cnpj,
          organizationNameRef.current,
        );
        if (generation !== generationRef.current) return;

        if (!resources.ok) {
          setResourcesPreloaded(false);
          setPreloadFailed(true);
          setDispensesPreloaded(false);
          setEcommerceResourcesPreloadStatus("failed");
          return;
        }

        setResourcesPreloaded(true);
        setDispensesPreloaded(resources.dispensesPreloaded);
        setEcommerceResourcesPreloadStatus("success");
        setFilterTick((tick) => tick + 1);
      } catch (preloadError) {
        console.error("Pré-carga ecommerce resources falhou", preloadError);
        if (generation === generationRef.current) {
          setResourcesPreloaded(false);
          setPreloadFailed(true);
          setDispensesPreloaded(false);
          setEcommerceResourcesPreloadStatus("failed");
        }
      }
    } catch {
      if (generation !== generationRef.current) return;
      setHasFetchedOnce(true);
      setEcommerceResourcesPreloadStatus("failed");
    }
  }, [cnpj, runLoadPatients, runPreload]);

  // listRevision: incrementado ao sair de /ecommerce/gerenciar.
  // Só reavalia chips via cache; não dispara GET por si.
  useEffect(() => {
    if (listRevision > 0) bumpFilterTick();
  }, [listRevision, bumpFilterTick]);

  useEffect(() => {
    if (!cnpj) {
      generationRef.current += 1;
      setPatients(EMPTY_PATIENTS);
      setResourcesPreloaded(false);
      setPreloadFailed(false);
      setDispensesPreloaded(false);
      setHasFetchedOnce(false);
      return;
    }

    void fetchPatients();

    return () => {
      generationRef.current += 1;
    };
  }, [cnpj, fetchPatients]);

  const hasLoadError = Boolean(loadError);
  const isInitialLoading =
    Boolean(cnpj) && (!hasFetchedOnce || (loading && patients.length === 0));
  const isListReady = hasFetchedOnce && !loading;
  const isPreloading =
    hasFetchedOnce &&
    !hasLoadError &&
    patients.length > 0 &&
    !resourcesPreloaded &&
    !preloadFailed;
  const isFullyReady =
    isListReady &&
    (resourcesPreloaded || preloadFailed || patients.length === 0);

  return {
    cnpj,
    patients,
    loading,
    error: hasLoadError,
    resourcesPreloaded,
    preloadFailed,
    dispensesPreloaded,
    isInitialLoading,
    isPreloading,
    isListReady,
    isFullyReady,
    hasFetchedOnce,
    filterTick,
    bumpFilterTick,
    fetchPatients,
  };
}
